import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  Alert,
  TextField,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
  Chip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm } from 'react-hook-form';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { getUserParameters, clearParametersCache } from '../services/userParametersService';
import { getAllBikeConfigs, getBikeTypeLabel, getFieldLabels } from '../services/bikeConfigService';
import { getRunningConfigurations, createRunningConfiguration, updateRunningConfiguration } from '../api/runningApi';
import { clearRunningConfigCache } from '../services/runningConfigService';

const Profile = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedSection, setSelectedSection] = useState('profile');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [userParams, setUserParams] = useState(null);
  const [loadingParams, setLoadingParams] = useState(false);
  const [bikeConfigs, setBikeConfigs] = useState([]);
  const [loadingBikeConfigs, setLoadingBikeConfigs] = useState(false);

  // Running config state
  const [runConfigId, setRunConfigId] = useState(null);
  const [runBodyWeight, setRunBodyWeight] = useState(70.0);
  const [runThresholdPace, setRunThresholdPace] = useState(5.0);
  const [runThresholdHR, setRunThresholdHR] = useState('');
  const [runMaxHR, setRunMaxHR] = useState(185);
  const [runRestingHR, setRunRestingHR] = useState('');
  const [loadingRunConfig, setLoadingRunConfig] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const newPassword = watch('newPassword', '');

  // Password validation checks
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);

  const sections = [
    {
      id: 'profile',
      label: t('profile') || 'Profile',
      icon: <PersonIcon />
    },
    {
      id: 'bike-defaults',
      label: t('bikeDefaults') || 'Bike Defaults',
      icon: <DirectionsBikeIcon />
    },
    {
      id: 'parameters',
      label: t('commonParametersOnly') || 'Common Parameters',
      icon: <SettingsIcon />
    },
    {
      id: 'running',
      label: 'Running',
      icon: <DirectionsRunIcon />
    },
    {
      id: 'change-password',
      label: t('changePassword') || 'Change Password',
      icon: <LockIcon />
    },
    {
      id: 'logout',
      label: t('logout') || 'Logout',
      icon: <LogoutIcon />
    }
  ];

  // Load bike configurations when Bike Defaults section is selected
  useEffect(() => {
    const loadBikeConfigs = async () => {
      if (selectedSection === 'bike-defaults') {
        setLoadingBikeConfigs(true);
        try {
          const configs = await getAllBikeConfigs();
          setBikeConfigs(configs);
        } catch (err) {
          console.error('Error loading bike configurations:', err);
          setError('Failed to load bike configurations');
        } finally {
          setLoadingBikeConfigs(false);
        }
      }
    };
    loadBikeConfigs();
  }, [selectedSection]);

  // Load user parameters when Parameters section is selected
  useEffect(() => {
    const loadParameters = async () => {
      if (selectedSection === 'parameters') {
        setLoadingParams(true);
        try {
          const params = await getUserParameters(true);
          setUserParams(params);
        } catch (err) {
          console.error('Error loading parameters:', err);
          setError('Failed to load parameters');
        } finally {
          setLoadingParams(false);
        }
      }
    };
    loadParameters();
  }, [selectedSection]);

  // Load running config when Running section is selected
  useEffect(() => {
    if (selectedSection !== 'running') return;
    setLoadingRunConfig(true);
    getRunningConfigurations()
      .then((configs) => {
        if (configs && configs.length > 0) {
          const c = configs[0];
          setRunConfigId(c.id);
          setRunBodyWeight(c.body_weight || 70.0);
          setRunThresholdPace(c.threshold_pace || 5.0);
          setRunThresholdHR(c.threshold_heart_rate || '');
          setRunMaxHR(c.max_heart_rate || 185);
          setRunRestingHR(c.resting_heart_rate || '');
        }
      })
      .catch(() => setError('Failed to load running configuration'))
      .finally(() => setLoadingRunConfig(false));
  }, [selectedSection]);

  const handleSaveRunningConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const config = {
        body_weight: parseFloat(runBodyWeight),
        threshold_pace: parseFloat(runThresholdPace),
        threshold_heart_rate: runThresholdHR ? parseInt(runThresholdHR, 10) : null,
        max_heart_rate: parseInt(runMaxHR, 10),
        resting_heart_rate: runRestingHR ? parseInt(runRestingHR, 10) : null,
      };
      if (runConfigId) {
        await updateRunningConfiguration(runConfigId, config);
      } else {
        const created = await createRunningConfiguration(config);
        setRunConfigId(created.id);
      }
      clearRunningConfigCache();
      setSuccess('Running configuration saved successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save running configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSectionClick = (sectionId) => {
    if (sectionId === 'logout') {
      logout();
      navigate('/login');
    } else {
      setSelectedSection(sectionId);
      setError('');
      setSuccess('');
    }
  };

  const onChangePasswordSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/auth/change-password', {
        current_password: data.currentPassword,
        new_password: data.newPassword
      });

      setSuccess(t('passwordChangedSuccess') || 'Password changed successfully!');

      // Clear form and redirect after 2 seconds
      setTimeout(() => {
        setSelectedSection('profile');
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Change password error:', err);
      setError(err.response?.data?.detail || t('passwordChangeError') || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (selectedSection) {
      case 'profile':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
              {t('profileInformation') || 'Profile Information'}
            </Typography>

            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: colors.grey[800],
                borderRadius: 2
              }}
            >
              <Box mb={2}>
                <Typography variant="body2" color={colors.grey[400]} mb={0.5}>
                  {t('email') || 'Email'}
                </Typography>
                <Typography variant="h6" color={colors.grey[100]}>
                  {user?.email || 'Not available'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color={colors.grey[400]} mb={0.5}>
                  {t('accountStatus') || 'Account Status'}
                </Typography>
                <Typography variant="h6" color={user?.is_verified ? colors.greenAccent[400] : colors.warnings.orange}>
                  {user?.is_verified ? (t('verified') || 'Verified') : (t('notVerified') || 'Not Verified')}
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      case 'bike-defaults':
        const bikeTypes = ['road', 'road_aero', 'tt', 'gravel'];

        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
              {t('bikeDefaults') || 'Bike Defaults'}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {t('bikeDefaultsInfo') || 'These are your saved bike configurations. They are automatically saved when you analyze a workout with a specific bike type and will be used as defaults for future analyses.'}
              </Typography>
            </Alert>

            {loadingBikeConfigs ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={3}>
                {bikeTypes.map((bikeType) => {
                  const config = bikeConfigs.find(c => c.bike_type === bikeType);
                  const fieldLabels = getFieldLabels(bikeType);

                  return (
                    <Paper
                      key={bikeType}
                      elevation={2}
                      sx={{
                        p: 3,
                        backgroundColor: colors.grey[800],
                        borderRadius: 2,
                        border: config ? `2px solid ${colors.blueAccent[700]}` : `1px solid ${colors.grey[700]}`
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" color={colors.grey[100]}>
                          {bikeType === 'road' ? t('roadBike') || 'Road Bike' :
                           bikeType === 'road_aero' ? t('roadBikeAero') || 'Road Bike with Aerobars' :
                           bikeType === 'tt' ? t('timeTrialBike') || 'Time Trial Bike' :
                           bikeType === 'gravel' ? t('gravelBike') || 'Gravel Bike' :
                           getBikeTypeLabel(bikeType)}
                        </Typography>
                        {config ? (
                          <Chip
                            label={t('configured') || 'Configured'}
                            size="small"
                            sx={{
                              backgroundColor: colors.greenAccent[700],
                              color: colors.grey[100]
                            }}
                          />
                        ) : (
                          <Chip
                            label={t('notConfigured') || 'Not Configured'}
                            size="small"
                            sx={{
                              backgroundColor: colors.grey[700],
                              color: colors.grey[300]
                            }}
                          />
                        )}
                      </Box>

                      {config ? (
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                          {Object.entries(fieldLabels).map(([field, label]) => {
                            const value = config[field];
                            if (value === null || value === undefined) return null;

                            const isSpeed = field.includes('speed');
                            const isCda = field.includes('cda');
                            const isWeight = field.includes('weight');

                            let displayValue = value;
                            if (isSpeed) displayValue = `${value.toFixed(0)} km/h`;
                            else if (isCda) displayValue = value.toFixed(2);
                            else if (isWeight) displayValue = `${value.toFixed(1)} kg`;
                            else displayValue = value.toFixed(2);

                            // Translate field labels
                            let translatedLabel = label;
                            if (field === 'bike_weight') translatedLabel = t('bikeWeight') || label;
                            else if (field === 'road_cda_hoods') translatedLabel = t('cdaHoods') || label;
                            else if (field === 'road_cda_drops') translatedLabel = t('cdaDrops') || label;
                            else if (field === 'road_drops_min_speed') translatedLabel = t('minSpeedForDrops') || label;
                            else if (field === 'road_aero_cda_hoods') translatedLabel = t('cdaHoods') || label;
                            else if (field === 'road_aero_cda_aerobar') translatedLabel = t('cdaAerobar') || label;
                            else if (field === 'road_aero_cda_drops') translatedLabel = t('cdaDrops') || label;
                            else if (field === 'road_aero_aerobar_min_speed') translatedLabel = t('minSpeedForAerobar') || label;
                            else if (field === 'road_aero_aerobar_max_speed') translatedLabel = t('maxSpeedForAerobar') || label;
                            else if (field === 'road_aero_drops_min_speed') translatedLabel = t('minSpeedForDrops') || label;
                            else if (field === 'tt_cda_base') translatedLabel = t('cdaBase') || label;
                            else if (field === 'tt_cda_aero') translatedLabel = t('cdaAero') || label;
                            else if (field === 'tt_aero_min_speed') translatedLabel = t('minSpeedForAero') || label;
                            else if (field === 'tt_aero_max_speed') translatedLabel = t('maxSpeedForAero') || label;
                            else if (field === 'gravel_cda_hoods') translatedLabel = t('cdaHoods') || label;
                            else if (field === 'gravel_cda_drops') translatedLabel = t('cdaDrops') || label;
                            else if (field === 'gravel_drops_min_speed') translatedLabel = t('minSpeedForDrops') || label;

                            return (
                              <Box key={field}>
                                <Typography variant="body2" color={colors.grey[400]}>
                                  {translatedLabel}
                                </Typography>
                                <Typography variant="body1" color={colors.grey[100]} fontWeight="500">
                                  {displayValue}
                                </Typography>
                              </Box>
                            );
                          })}

                          <Box gridColumn={{ xs: '1', sm: '1 / -1' }}>
                            <Typography variant="caption" color={colors.grey[500]}>
                              {t('lastUpdated') || 'Last updated'}: {new Date(config.updated_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color={colors.grey[400]}>
                          {t('notConfiguredYet') || 'Not configured yet. This bike type will be configured automatically when you analyze a workout with it.'}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        );

      case 'parameters':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
              {t('commonParametersOnly') || 'Common Parameters'}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {t('commonParametersInfo') || 'These are your common physics parameters used across all bike types. They are automatically updated when you submit a new analysis.'}
              </Typography>
            </Alert>

            {loadingParams ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : userParams ? (
              <>
                {/* Common Parameters Only */}
                <Paper elevation={2} sx={{ p: 3, backgroundColor: colors.grey[800], borderRadius: 2, mb: 3 }}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="body2" color={colors.grey[400]}>
                        {t('riderWeight') || 'Rider Weight'}
                      </Typography>
                      <Typography variant="h6" color={colors.grey[100]}>
                        {userParams.rider_weight?.toFixed(1) || 'Not set'} kg
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color={colors.grey[400]}>
                        {t('airDensity') || 'Air Density'}
                      </Typography>
                      <Typography variant="h6" color={colors.grey[100]}>
                        {userParams.air_density?.toFixed(3) || 'Not set'} kg/m³
                      </Typography>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      {t('rollingResistanceNote') || 'Note: Rolling resistance is automatically calculated based on your bike type and terrain settings.'}
                    </Typography>
                  </Alert>
                </Paper>

                <Button
                  variant="outlined"
                  onClick={() => {
                    clearParametersCache();
                    setSuccess(t('parametersCacheCleared') || 'Parameters cache cleared. They will be reloaded on next analysis.');
                    setTimeout(() => setSuccess(''), 3000);
                  }}
                  sx={{
                    color: colors.grey[100],
                    borderColor: colors.primary[400],
                    '&:hover': {
                      borderColor: colors.primary[300],
                      backgroundColor: colors.primary[700]
                    }
                  }}
                >
                  {t('clearCache') || 'Clear Cache'}
                </Button>
              </>
            ) : (
              <Alert severity="warning">
                {t('noParametersFound') || 'No parameters found. Parameters will be created when you submit your first analysis.'}
              </Alert>
            )}
          </Box>
        );

      case 'change-password':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
              {t('changePassword') || 'Change Password'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: colors.grey[800],
                borderRadius: 2
              }}
            >
              <form onSubmit={handleSubmit(onChangePasswordSubmit)}>
                <TextField
                  fullWidth
                  label={t('currentPassword') || 'Current Password'}
                  type="password"
                  margin="normal"
                  {...register('currentPassword', {
                    required: t('currentPasswordRequired') || 'Current password is required'
                  })}
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label={t('newPassword') || 'New Password'}
                  type="password"
                  margin="normal"
                  {...register('newPassword', {
                    required: t('newPasswordRequired') || 'New password is required',
                    validate: {
                      minLength: (v) => v.length >= 8 || t('passwordMinLength') || 'Password must be at least 8 characters',
                      hasUppercase: (v) => /[A-Z]/.test(v) || t('passwordNeedsUppercase') || 'Password must contain at least one uppercase letter',
                      hasLowercase: (v) => /[a-z]/.test(v) || t('passwordNeedsLowercase') || 'Password must contain at least one lowercase letter',
                      hasNumber: (v) => /\d/.test(v) || t('passwordNeedsNumber') || 'Password must contain at least one number'
                    }
                  })}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label={t('confirmNewPassword') || 'Confirm New Password'}
                  type="password"
                  margin="normal"
                  {...register('confirmPassword', {
                    required: t('confirmPasswordRequired') || 'Please confirm your password',
                    validate: (value) =>
                      value === newPassword || t('passwordsDoNotMatch') || 'Passwords do not match'
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  disabled={loading}
                />

                {/* Password requirements checklist */}
                <Box mt={2} mb={2}>
                  <Typography variant="body2" color={colors.grey[100]} mb={1}>
                    {t('passwordRequirements') || 'Password Requirements'}:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {hasMinLength ?
                          <CheckCircleIcon color="success" fontSize="small" /> :
                          <CancelIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={t('atLeast8Characters') || 'At least 8 characters'}
                        primaryTypographyProps={{ variant: 'body2', color: colors.grey[300] }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {hasUppercase ?
                          <CheckCircleIcon color="success" fontSize="small" /> :
                          <CancelIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={t('oneUppercaseLetter') || 'One uppercase letter'}
                        primaryTypographyProps={{ variant: 'body2', color: colors.grey[300] }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {hasLowercase ?
                          <CheckCircleIcon color="success" fontSize="small" /> :
                          <CancelIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={t('oneLowercaseLetter') || 'One lowercase letter'}
                        primaryTypographyProps={{ variant: 'body2', color: colors.grey[300] }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {hasNumber ?
                          <CheckCircleIcon color="success" fontSize="small" /> :
                          <CancelIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={t('oneNumber') || 'One number'}
                        primaryTypographyProps={{ variant: 'body2', color: colors.grey[300] }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  {loading ? t('changingPassword') || 'Changing Password...' : t('changePassword') || 'Change Password'}
                </Button>
              </form>
            </Paper>
          </Box>
        );

      case 'running':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
              Running Configuration
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {loadingRunConfig ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <Paper elevation={2} sx={{ p: 3, backgroundColor: colors.grey[800], borderRadius: 2 }}>
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={3}>
                  <TextField
                    label={t('bodyWeight', 'Body Weight (kg)')}
                    type="number"
                    value={runBodyWeight}
                    onChange={(e) => setRunBodyWeight(e.target.value)}
                    inputProps={{ step: 0.1, min: 40, max: 150 }}
                    fullWidth
                    disabled={loading}
                  />
                  <TextField
                    label={t('thresholdPace', 'Threshold Pace (min/km)')}
                    type="number"
                    value={runThresholdPace}
                    onChange={(e) => setRunThresholdPace(e.target.value)}
                    inputProps={{ step: 0.1, min: 3, max: 10 }}
                    helperText="Your best recent 5K pace + ~15s/km is a good estimate"
                    fullWidth
                    disabled={loading}
                  />
                  <TextField
                    label={t('thresholdHR', 'Threshold Heart Rate (bpm)')}
                    type="number"
                    value={runThresholdHR}
                    onChange={(e) => setRunThresholdHR(e.target.value)}
                    inputProps={{ min: 100, max: 220 }}
                    helperText="Average HR during a 30-min all-out effort"
                    fullWidth
                    disabled={loading}
                  />
                  <TextField
                    label={t('maxHR', 'Max Heart Rate (bpm)')}
                    type="number"
                    value={runMaxHR}
                    onChange={(e) => setRunMaxHR(e.target.value)}
                    inputProps={{ min: 120, max: 220 }}
                    helperText="220 - age is a rough estimate"
                    fullWidth
                    disabled={loading}
                  />
                  <TextField
                    label={t('restingHR', 'Resting Heart Rate (bpm)')}
                    type="number"
                    value={runRestingHR}
                    onChange={(e) => setRunRestingHR(e.target.value)}
                    inputProps={{ min: 30, max: 100 }}
                    helperText="Optional"
                    fullWidth
                    disabled={loading}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleSaveRunningConfig}
                  disabled={loading}
                  fullWidth
                  size="large"
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Running Configuration'}
                </Button>
              </Paper>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // Redirect if not logged in
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <Box m="20px" display="flex" justifyContent="center">
      <Box
        maxWidth="1200px"
        width="100%"
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={3}
      >
        {/* Sidebar */}
        <Paper
          elevation={3}
          sx={{
            width: { xs: '100%', md: '280px' },
            height: 'fit-content',
            backgroundColor: colors.grey[800],
            borderRadius: 2,
            position: { xs: 'static', md: 'sticky' },
            top: '20px'
          }}
        >
          <Box p={2} borderBottom={`1px solid ${colors.grey[700]}`} display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
              {t('userSettings') || 'User Settings'}
            </Typography>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: colors.grey[300] }}
              aria-label="Go back"
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>

          <List>
            {sections.map((section) => (
              <ListItem key={section.id} disablePadding>
                <ListItemButton
                  onClick={() => handleSectionClick(section.id)}
                  selected={selectedSection === section.id}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: colors.blueAccent[800],
                      '&:hover': {
                        backgroundColor: colors.blueAccent[700]
                      }
                    },
                    '&:hover': {
                      backgroundColor: colors.grey[700]
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: colors.blueAccent[400] }}>
                    {section.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" color={colors.grey[100]}>
                        {section.label}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Main Content */}
        <Box flex={1}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
